import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  IconButton,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NoteIcon from '@mui/icons-material/Note';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { getPackageOrderById, PackageOrderDto, PackageOrderItemDto } from '../../../../services/salesService';
import { printPackageReceipt, PackageReceiptData, PackageReceiptItem } from '../components/PackageReceiptPrintService';
import { resolveReceiptHeader, resolveReceiptLabel, buildReceiptOrderRef } from '../utils/receiptHeader';
import { ORDER_SOURCE_TO_PLATFORM } from '../utils/helpers';
import type { PlatformIntegration } from '../../../../types/platformIntegration';

// ============================================================================
// TYPES
// ============================================================================

type OrderSource = 'phone' | 'yemeksepeti' | 'trendyol' | 'getir' | 'migros' | 'kiosk' | 'qr' | 'ecommerce';
type OrderStatus = 'new' | 'preparing' | 'ready' | 'onway' | 'delivered' | 'cancelled';

interface OrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | null;
  onKitchenPrint?: (orderId: number, source?: string) => void;
  onReceiptPrint?: (orderId: number) => void;
  /**
   * Online ödenmiş telefon siparisleri için Pavo cihazından mali fis kestirme
   * handler'i. Tanimliysa ve siparisin PaymentStatus=PAID + PaymentMethod
   * "online" iceriyorsa "Online Fis Yazdir" butonu gorunur.
   */
  onRequestOnlineReceipt?: () => void;
  /**
   * PR1: Telefon siparişi iptal akışı.
   * Tanımlıysa ve `cancelAllowed === true` ise, DialogActions'ta kırmızı
   * "İptal Et" butonu görünür. Buton parent'taki CancelPhoneOrderModal'ı açar.
   * Permission/guard kararı parent'ta verildiği için buradaki UI sadece
   * görünürlük + tooltip ile kısıtlanır.
   */
  onCancelOrder?: () => void;
  cancelAllowed?: boolean;
  /** i18n key (ör: 'orderManagement.permissions.pavoActive') — disabled tooltip metni. */
  cancelDisabledReasonI18nKey?: string;
  /**
   * PR2: Telefon siparişi sepet düzenleme akışı.
   * Tanımlıysa "Sepeti Düzenle" butonu DialogActions'ta görünür.
   * `editCartAllowed === false` iken disabled + tooltip i18n key gösterilir.
   */
  onEditCart?: () => void;
  editCartAllowed?: boolean;
  editCartDisabledReasonI18nKey?: string;
  /**
   * PR2: Parent dışarıdan tetiklediği zaman (ör: EditCart modal'da kalem
   * silindi/güncellendi) içeride sipariş detayı yeniden çekilsin diye
   * monoton artan bir sayaç. Değer değişince useEffect tekrar tetiklenir.
   */
  refreshKey?: number;
  screenSettings?: {
    localServiceUrl?: string;
    printingSettings?: {
      defaultReceiptPrinter?: { id: string; name: string } | null;
      adisyonPrinter?: { id: string; name: string } | null;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
        standardFooterNote?: string;
        thankYouMessage?: string;
      };
    };
    /** Marka bazlı fiş başlığını çözebilmek için entegrasyon listesi. */
    platformIntegrations?: PlatformIntegration[];
  } | null;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getSourceIcon = (source: OrderSource) => {
  const iconStyle = { fontSize: 20 };
  switch (source) {
    case 'phone': return <PhoneIcon sx={iconStyle} />;
    case 'yemeksepeti': return <RestaurantMenuIcon sx={iconStyle} />;
    case 'trendyol': return <TwoWheelerIcon sx={iconStyle} />;
    case 'getir': return <ShoppingCartIcon sx={iconStyle} />;
    case 'migros': return <StorefrontIcon sx={iconStyle} />;
    case 'kiosk': return <PointOfSaleIcon sx={iconStyle} />;
    case 'qr': return <QrCode2Icon sx={iconStyle} />;
    case 'ecommerce': return <ShoppingBagIcon sx={iconStyle} />;
    default: return <PhoneIcon sx={iconStyle} />;
  }
};

const getSourceColor = (source: OrderSource): string => {
  switch (source) {
    case 'phone': return '#4CAF50';
    case 'yemeksepeti': return '#FA0050';
    case 'trendyol': return '#06C167';
    case 'getir': return '#5D3EBC';
    case 'migros': return '#F58220';
    case 'kiosk': return '#607D8B';
    case 'qr': return '#2196F3';
    case 'ecommerce': return '#9C27B0';
    default: return '#4CAF50';
  }
};

const getSourceName = (source: OrderSource): string => {
  switch (source) {
    case 'phone': return 'Telefon Siparişi';
    case 'yemeksepeti': return 'Yemeksepeti';
    case 'trendyol': return 'Uber Eats';
    case 'getir': return 'Getir Yemek';
    case 'migros': return 'Migros Yemek';
    case 'kiosk': return 'Kiosk Siparişi';
    case 'qr': return 'QR Menü Siparişi';
    case 'ecommerce': return 'E-Ticaret Siparişi';
    default: return 'Sipariş';
  }
};

const getStatusInfo = (status: string): { label: string; color: 'success' | 'warning' | 'info' | 'secondary' | 'default' | 'error'; emoji: string } => {
  switch (status?.toLowerCase()) {
    case 'new': 
    case 'pending': 
      return { label: 'Yeni', color: 'success', emoji: '🟢' };
    case 'accepted':
      return { label: 'Onaylandı', color: 'info', emoji: '✅' };
    case 'preparing': 
      return { label: 'Hazırlanıyor', color: 'warning', emoji: '🟡' };
    case 'ready': 
      return { label: 'Teslime Hazır', color: 'info', emoji: '🔵' };
    case 'onway': 
    case 'ontheway':
      return { label: 'Yolda', color: 'secondary', emoji: '🟣' };
    case 'delivered': 
    case 'completed':
      return { label: 'Teslim Edildi', color: 'default', emoji: '⚫' };
    case 'cancelled': 
      return { label: 'İptal Edildi', color: 'error', emoji: '❌' };
    default: 
      return { label: status || 'Bilinmiyor', color: 'default', emoji: '⚪' };
  }
};

const getPaymentStatusInfo = (status: string): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
  switch (status?.toLowerCase()) {
    case 'paid':
      return { label: 'Ödendi', color: 'success' };
    case 'partial':
      return { label: 'Kısmi Ödeme', color: 'warning' };
    case 'unpaid':
    case 'pending':
      return { label: 'Ödenmedi', color: 'error' };
    default:
      return { label: status || 'Belirsiz', color: 'default' };
  }
};

const formatDateTime = (dateStr: string): { date: string; time: string } => {
  if (!dateStr) return { date: '-', time: '-' };
  
  try {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('tr-TR'),
      time: date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
    };
  } catch {
    return { date: '-', time: '-' };
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ open, onClose, orderId, onKitchenPrint, onReceiptPrint, screenSettings, onRequestOnlineReceipt, onCancelOrder, cancelAllowed, cancelDisabledReasonI18nKey, onEditCart, editCartAllowed, editCartDisabledReasonI18nKey, refreshKey }) => {
  const { colors: themeColors } = useThemeContext();
  const { userDetails } = useAuth();
  const { t } = useTranslation('salesSystem');
  const companyId = userDetails?.companyId;

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<PackageOrderDto | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  // ============================================================================
  // LOAD ORDER DETAILS
  // ============================================================================

  const loadOrderDetails = useCallback(async () => {
    if (!orderId || !companyId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('📦 [OrderDetailModal] Sipariş detayı yükleniyor...', { orderId, companyId });
      
      const response = await getPackageOrderById(orderId);
      
      if (response.Success && response.Data) {
        console.log('📦 [OrderDetailModal] Sipariş yüklendi:', response.Data);
        setOrder(response.Data);
      } else {
        setError(response.Message || 'Sipariş detayları yüklenemedi');
      }
    } catch (err: any) {
      console.error('📦 [OrderDetailModal] Hata:', err);
      setError(err.message || 'Sipariş detayları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [orderId, companyId]);

  useEffect(() => {
    if (open && orderId) {
      // PR2: `refreshKey` parent'tan gelirse (ör: EditCart mutation) yeniden çek.
      loadOrderDetails();
    } else {
      // Modal kapandığında state'i temizle
      setOrder(null);
      setError(null);
    }
  }, [open, orderId, loadOrderDetails, refreshKey]);

  // ============================================================================
  // PRINT RECEIPT
  // ============================================================================

  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;

    const printer = screenSettings?.printingSettings?.defaultReceiptPrinter
      || screenSettings?.printingSettings?.adisyonPrinter;
    if (!printer) {
      alert('Fiş yazıcısı tanımlanmamış. Lütfen ekran ayarlarından yazıcı seçin.');
      return;
    }

    setIsPrinting(true);

    try {
      const localServiceUrl = screenSettings?.localServiceUrl || 'http://localhost:3003';
      const createdAtFormatted = formatDateTime(order.CreatedAt);

      // Ürünleri dönüştür
      const receiptItems: PackageReceiptItem[] = (order.Items || []).map(item => ({
        name: item.ProductName || 'Ürün',
        quantity: item.Quantity || 1,
        unitPrice: item.UnitPrice || 0,
        totalPrice: item.TotalAmount || (item.UnitPrice || 0) * (item.Quantity || 1),
      }));

      // Fiş verisi hazırla
      const orderSource = (order as any).Source || (order as any).source || 'phone';
      const platformLabels: Record<string, string> = {
        yemeksepeti: 'YEMEKSEPETI PAKET SIPARIS',
        getir: 'GETIR PAKET SIPARIS',
        trendyol: 'UBER EATS PAKET SIPARIS',
        migros: 'MIGROS PAKET SIPARIS',
        phone: 'TELEFON PAKET SIPARIS',
      };

      const discountAmount = (order as any).DiscountAmount || 0;

      // Markaya özel fiş başlığı; telefon siparişinin markası olmadığı için
      // çözümleyici orada ekran varsayılanını döndürür.
      const headerOrderRef = buildReceiptOrderRef(
        ORDER_SOURCE_TO_PLATFORM[orderSource] || '',
        null,
        order,
      );
      const brandHeader = resolveReceiptHeader(headerOrderRef, screenSettings);

      const receiptData: PackageReceiptData = {
        orderNumber: order.OrderNumber || `#${order.Id}`,
        shortCode: order.ShortCode,
        date: createdAtFormatted.date,
        time: createdAtFormatted.time,
        customerName: order.AccountName || 'Müşteri',
        customerPhone: order.CustomerPhone,
        customerAddress: order.CustomerAddress,
        // Backend AccountShippingAddress join'inden gelen detayli adres
        // alanlari (telefon paket fislerinde mahalle/cadde/sokak/apartman vb.
        // satirlarini fise basabilmek icin). Local service tarafi bu alanlari
        // tek tek satir olarak yazdirir.
        mahalle: order.Mahalle,
        cadde: order.Cadde,
        sokak: order.Sokak,
        apartman: order.Apartman,
        siteAdi: order.SiteAdi,
        blok: order.Blok,
        binaNo: order.BinaNo,
        kat: order.Kat,
        daireNo: order.DaireNo,
        items: receiptItems,
        subtotal: order.SubTotal || order.TotalAmount || 0,
        vatTotal: order.TaxAmount || 0,
        grandTotal: order.TotalAmount || 0,
        discountTotal: discountAmount > 0 ? discountAmount : undefined,
        paymentStatus: order.PaymentStatus,
        paymentMethod: order.PaymentMethod || undefined,
        isOnlinePaid: order.PaymentStatus === 'PAID' && (order.PaymentMethod || '').toLowerCase().includes('online'),
        orderNote: order.CustomerNotes,
        ...brandHeader,
        companyInfo: brandHeader.companyInfo
          ? { ...brandHeader.companyInfo, name: brandHeader.companyInfo.name || 'AQUASOFT' }
          : undefined,
        logoMaxWidthPx: brandHeader.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: brandHeader.logoMaxHeightPx ?? 150,
        receiptLabel: resolveReceiptLabel(
          platformLabels[orderSource] || 'PAKET SIPARIS',
          headerOrderRef,
          screenSettings,
        ),
      };

      console.log('🧾 [OrderDetailModal] Fiş yazdırılıyor:', {
        orderId: order.Id,
        printer: printer.name,
        itemCount: receiptItems.length,
        total: receiptData.grandTotal
      });

      await printPackageReceipt(receiptData, printer, localServiceUrl);
      
      console.log('✅ [OrderDetailModal] Fiş yazdırıldı');
      
    } catch (err: any) {
      console.error('❌ [OrderDetailModal] Fiş yazdırma hatası:', err);
      alert(`Fiş yazdırma hatası: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [order, screenSettings]);

  // ============================================================================
  // RENDER - Loading State
  // ============================================================================

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary" mt={2}>
              Sipariş detayları yükleniyor...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // ============================================================================
  // RENDER - Error State
  // ============================================================================

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Sipariş Detayı</Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert 
            severity="error" 
            action={
              <Button color="inherit" size="small" onClick={loadOrderDetails} startIcon={<RefreshIcon />}>
                Tekrar Dene
              </Button>
            }
          >
            {error}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Kapat</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ============================================================================
  // RENDER - No Order
  // ============================================================================

  if (!order) {
    return null;
  }

  // ============================================================================
  // ORDER DATA
  // ============================================================================

  const source: OrderSource = 'phone'; // Şimdilik telefon siparişi olarak kabul et
  const sourceColor = getSourceColor(source);
  const statusInfo = getStatusInfo(order.OrderStatus || 'new');
  const paymentInfo = getPaymentStatusInfo(order.PaymentStatus || 'unpaid');
  const createdAt = formatDateTime(order.CreatedAt);
  const items = order.Items || [];

  // Hesaplamalar
  const totalAmount = order.TotalAmount || 0;

  // ============================================================================
  // RENDER - Order Details
  // ============================================================================

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderTop: `4px solid ${sourceColor}` }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: sourceColor, width: 40, height: 40 }}>
              {getSourceIcon(source)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Sipariş #{order.ShortCode || order.OrderNumber || order.Id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {getSourceName(source)}
                {order.OrderNumber && order.OrderNumber !== order.ShortCode && ` • ${order.OrderNumber}`}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label={`${statusInfo.emoji} ${statusInfo.label}`}
              color={statusInfo.color}
              size="small"
            />
            <Chip 
              label={paymentInfo.label}
              color={paymentInfo.color}
              size="small"
              variant="outlined"
            />
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Sol Kolon - Müşteri & Teslimat Bilgileri */}
          <Grid item xs={12} md={5}>
            {/* Müşteri Bilgileri */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" /> Müşteri Bilgileri
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2">
                  <strong>Ad Soyad:</strong> {order.AccountName || '-'}
                </Typography>
                {order.CustomerPhone && (
                  <Typography variant="body2">
                    <strong>Telefon:</strong> {order.CustomerPhone}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Teslimat Bilgileri */}
            {order.CustomerAddress && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon fontSize="small" /> Teslimat Adresi
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {order.CustomerAddress}
                </Typography>
                {order.CustomerNotes && (
                  <Box mt={1} p={1} bgcolor="warning.light" borderRadius={1}>
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <NoteIcon fontSize="small" /> {order.CustomerNotes}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}

            {/* Sipariş Bilgileri */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTimeIcon fontSize="small" /> Sipariş Bilgileri
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body2">
                  <strong>Tarih:</strong> {createdAt.date}
                </Typography>
                <Typography variant="body2">
                  <strong>Saat:</strong> {createdAt.time}
                </Typography>
                {order.WaitingMinutes !== undefined && order.WaitingMinutes > 0 && (
                  <Typography variant="body2" color={order.WaitingMinutes > 30 ? 'error.main' : 'text.secondary'}>
                    <strong>Bekleme Süresi:</strong> {order.WaitingMinutes} dk
                  </Typography>
                )}
                {order.EstimatedDeliveryTime && (
                  <Typography variant="body2">
                    <strong>Tahmini Teslimat:</strong> {formatDateTime(order.EstimatedDeliveryTime).time}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Ödeme Yöntemi:</strong> {order.PaymentMethod || 'Kapıda Ödeme'}
                </Typography>
                {order.CreatedByName && (
                  <Typography variant="body2">
                    <strong>Oluşturan:</strong> {order.CreatedByName}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Kurye Bilgisi - Yolda olan siparişler için */}
            {order.CourierName && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'secondary.light' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  🛵 Kurye Bilgisi
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong>Kurye:</strong> {order.CourierName}
                </Typography>
                {order.CourierPhone && (
                  <Typography variant="body2">
                    <strong>Telefon:</strong> {order.CourierPhone}
                  </Typography>
                )}
              </Paper>
            )}

            {/* İptal Bilgisi */}
            {order.OrderStatus?.toLowerCase() === 'cancelled' && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'error.light' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="error.dark">
                  ❌ İptal Bilgisi
                </Typography>
                <Divider sx={{ my: 1 }} />
                {order.CancelledAt && (
                  <Typography variant="body2">
                    <strong>İptal Tarihi:</strong> {formatDateTime(order.CancelledAt).date} {formatDateTime(order.CancelledAt).time}
                  </Typography>
                )}
                {order.CancelReason && (
                  <Typography variant="body2">
                    <strong>İptal Sebebi:</strong> {order.CancelReason}
                  </Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Sağ Kolon - Ürünler & Fiyatlar */}
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📦 Sipariş Detayı ({items.length} Ürün)
              </Typography>
              <Divider sx={{ my: 1 }} />
              
              {items.length === 0 ? (
                <Box py={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    Ürün bilgisi bulunamadı
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Ürün</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: 50 }}>Adet</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', width: 80 }}>B.Fiyat</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', width: 80 }}>Toplam</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={item.Id || index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.ProductName || `Ürün ${index + 1}`}
                            </Typography>
                            {item.Notes && (
                              <Typography variant="caption" color="warning.main" display="block">
                                📝 {item.Notes}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.Quantity || 1}</TableCell>
                          <TableCell align="right">{(item.UnitPrice || 0).toFixed(2)} ₺</TableCell>
                          <TableCell align="right">{(item.TotalAmount || 0).toFixed(2)} ₺</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Toplam Bilgileri */}
              <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
                <Typography variant="h6" fontWeight="bold" color={themeColors.primary}>
                  Genel Toplam: {totalAmount.toFixed(2)} ₺
                </Typography>
                
                {/* Ödeme Bilgisi */}
                {order.TotalPaid !== undefined && order.TotalPaid > 0 && (
                  <>
                    <Typography variant="body2" color="success.main">
                      Ödenen: {order.TotalPaid.toFixed(2)} ₺
                    </Typography>
                    {order.RemainingAmount !== undefined && order.RemainingAmount > 0 && (
                      <Typography variant="body2" color="error.main" fontWeight="bold">
                        Kalan: {order.RemainingAmount.toFixed(2)} ₺
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          startIcon={<RefreshIcon />}
          onClick={loadOrderDetails}
        >
          Yenile
        </Button>
        <Button 
          startIcon={<ContentCopyIcon />}
          onClick={() => navigator.clipboard.writeText(`Sipariş #${order.ShortCode || order.Id}`)}
        >
          Kopyala
        </Button>
        <Button 
          startIcon={<RestaurantMenuIcon />}
          variant="outlined"
          color="warning"
          onClick={() => orderId && onKitchenPrint?.(orderId, 'phone')}
          disabled={!onKitchenPrint}
        >
          Mutfağa Yazdır
        </Button>
        <Button 
          startIcon={<PrintIcon />}
          variant="outlined"
          onClick={handlePrintReceipt}
          disabled={isPrinting || (!screenSettings?.printingSettings?.defaultReceiptPrinter && !screenSettings?.printingSettings?.adisyonPrinter)}
        >
          {isPrinting ? 'Yazdırılıyor...' : 'Fiş Yazdır'}
        </Button>
        {/* Online ödenmiş telefon siparisleri icin REST-P2P uzerinden Pavo
            cihazindan mali fis kestirme. PaymentStatus=PAID + PaymentMethod
            "online" iceriyorsa gorunur. */}
        {onRequestOnlineReceipt && order && order.PaymentStatus === 'PAID'
          && (order.PaymentMethod || '').toLowerCase().includes('online') && (
          <Button
            variant="contained"
            color="success"
            startIcon={<ReceiptLongIcon />}
            onClick={onRequestOnlineReceipt}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            Online Fiş Yazdır
          </Button>
        )}
        {/* PR2: Telefon siparişi sepet düzenleme butonu.
            `editCartAllowed` parent'ta `canEditPhoneOrderItems` ile hesaplanır:
            phone-only + status/kurye/ödeme/Pavo guard'ları + kurye yoksa.
            Disabled iken tooltip i18n key'i gösterilir. */}
        {onEditCart && order && (
          <Tooltip
            title={!editCartAllowed && editCartDisabledReasonI18nKey ? t(editCartDisabledReasonI18nKey) : ''}
            arrow
            disableHoverListener={editCartAllowed}
            disableFocusListener={editCartAllowed}
          >
            <span>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditNoteIcon />}
                onClick={onEditCart}
                disabled={!editCartAllowed}
              >
                {t('orderManagement.editPhoneOrderItems.title')}
              </Button>
            </span>
          </Tooltip>
        )}
        {/* PR1: Telefon siparişi iptal butonu.
            Parent (PackageOrderScreen) zaten `cancelAllowed` boolean'ını
            `canCancelPhoneOrder` üzerinden hesaplar; bu fonksiyon ilk guard
            olarak `source !== 'phone'` kontrolünü yapar. Modal phone-only
            kullanıldığı için pratikte buton hep görünür; başka kaynaktan
            açılırsa "yalnızca telefon siparişleri" tooltip'i ile disabled olur. */}
        {onCancelOrder && order && (
          <Tooltip
            title={!cancelAllowed && cancelDisabledReasonI18nKey ? t(cancelDisabledReasonI18nKey) : ''}
            arrow
            disableHoverListener={cancelAllowed}
            disableFocusListener={cancelAllowed}
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlinedIcon />}
                onClick={onCancelOrder}
                disabled={!cancelAllowed}
              >
                {t('orderManagement.cancelPhoneOrder.buttons.confirm')}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button 
          variant="contained" 
          onClick={onClose}
          sx={{ bgcolor: themeColors.primary }}
        >
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailModal;
